import { useMemo, useState } from 'react';
import type { Issue, Area } from '../../types';
import { STATUS_LABELS, STATUS_ORDER, KIND_ORDER, KIND_LABEL, type IssueSize } from '../../types';
import { displayName, initials, type Profiles } from '../../people';
import VendorTag from '../VendorTag';
import { blockerOf } from '../../blocked';
import { rollUpEpic, latestUpdateAcross, type EpicHealth } from '../../epic-rollup';
import { pickDefaultPivot } from '../../pivot-default';
import { MONTHS } from '../../when';
import { steadyNow } from '../../steady-now';
import { IssueTextInline } from '../IssueText';

/**
 * AREA DETAIL, ACROSS THE WORKSPACE — the Portfolio's By-area drill, the
 * permanent-taxonomy twin of the epic detail (EpicDetail.tsx). An epic asks "how
 * is this one initiative moving, to its finish line"; an area asks "where does the
 * work in this part of the estate stand, and who owns it". So this screen mirrors
 * the epic detail's shape — a header that rolls owner, open work, progress and
 * health up from the cards, the latest update, then the cards re-pivoted — but
 * drops the two things that only make sense for a time-boxed thing: there is NO
 * Due / slip cell (a standing part of the product has no finish line, so a date
 * would be a lie) and NO lifecycle band. In their place the header carries Open
 * work and Blocked.
 *
 * KEYED BY NAME, across boards. A card's `area` is a board-local id (board A's
 * "a1" is not board B's), so members are resolved the way the by-area lens
 * resolves them (src/area-rollup.ts): each card's id → its area NAME through its
 * own board's area list, and everything that shares the name is one area. The
 * cards re-pivot By board / By status / By kind — the cuts that stay meaningful
 * across boards. By phase is NOT offered: a phase is a board's own, so grouping
 * phases from different boards would collide — the same reason the epic detail
 * leaves phase on the board.
 *
 * DERIVED. Owner (plurality), health, progress, open/total and needs-attention
 * are read from the member cards by the SAME rollUpEpic the epics and the area
 * lens use, so an area and an epic never compute "at risk" or "62%" two different
 * ways. A STORED area lead will be preferred over the derived owner once the
 * `areas.lead` column lands — until then the header shows the derived owner,
 * tagged "auto", and is never empty. A card opens in the Portfolio's own lean
 * panel (onOpenCard), never leaving the portfolio.
 */

const HEALTH_WORD: Record<EpicHealth, string> = { ontrack: 'On track', atrisk: 'At risk', offtrack: 'Off track' };
const HEALTH_CLS: Record<EpicHealth, string> = { ontrack: 'g', atrisk: 'a', offtrack: 'r' };
const SIZE_LETTER: Record<IssueSize, string> = { quick: 'S', medium: 'M', long: 'L' };

type Pivot = 'board' | 'status' | 'kind';
const PIVOT_LABEL: Record<Pivot, string> = { board: 'By board', status: 'By status', kind: 'By kind' };
const PIVOT_SUB: Record<Pivot, string> = {
  board: 'the board they live on',
  status: 'where they are on their board',
  kind: 'what kind of work they are',
};

function fmtStamp(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function daysUntil(iso: string, now: number): number {
  const [y, m, d] = iso.split('-').map(Number);
  const due = Date.UTC(y, (m ?? 1) - 1, d ?? 1);
  const t = new Date(now);
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((due - today) / 86_400_000);
}

interface Props {
  name: string;
  cardsByBoard: Map<string, Issue[]>;
  areasByBoard: Map<string, Area[]>;
  boards: { id: string; name: string }[];
  profiles: Profiles;
  onBack: () => void;
  onOpenCard: (projectId: string, cardId: string) => void;
}

export default function AreaDetail({ name, cardsByBoard, areasByBoard, boards, profiles, onBack, onOpenCard }: Props) {
  // HELD STILL WITHIN THE MINUTE, so the memos below that take `now` as a
  // dependency actually cache (see src/steady-now.ts).
  const now = steadyNow();

  const boardName = useMemo(() => {
    const m = new Map(boards.map((b) => [b.id, b.name]));
    return (id: string) => m.get(id) ?? '—';
  }, [boards]);

  // Every member card across every board, resolved by area NAME through each
  // board's own area list, kept with the board it lives on so a click opens it
  // in place. The area's colour is taken from the first board that defines the
  // name — the same value the lens and the card rail use.
  const resolved = useMemo(() => {
    const out: { card: Issue; boardId: string }[] = [];
    let colour = '';
    let lead = '';
    for (const [boardId, cards] of cardsByBoard) {
      const areaById = new Map((areasByBoard.get(boardId) ?? []).map((a) => [a.id, a]));
      for (const c of cards) {
        const a = c.area ? areaById.get(c.area) : undefined;
        if (a && a.name === name) {
          out.push({ card: c, boardId });
          if (!colour && a.colour) colour = a.colour;
          if (!lead && a.lead && a.lead.trim()) lead = a.lead.trim();
        }
      }
    }
    return { members: out, colour, lead };
  }, [cardsByBoard, areasByBoard, name]);

  const members = resolved.members;
  const cards = useMemo(() => members.map((m) => m.card), [members]);
  const boardOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const { card, boardId } of members) m.set(card.id, boardId);
    return m;
  }, [members]);

  // Open on the most-populated cut (see src/pivot-default.ts), not a fixed one —
  // computed once from the cards on mount. Ties fall to status, then kind, then
  // board. The drill is keyed by area name in the Portfolio shell, so each area
  // mounts fresh and gets its own default.
  const [pivot, setPivot] = useState<Pivot>(() =>
    pickDefaultPivot<Pivot>(
      {
        status: new Set(cards.map((c) => c.status)).size,
        kind: new Set(cards.map((c) => c.kind)).size,
        board: new Set(members.map((m) => m.boardId)).size,
      },
      ['status', 'kind', 'board'],
    ),
  );

  const roll = useMemo(() => rollUpEpic(name, cards, [], now), [name, cards, now]);
  const latest = useMemo(() => latestUpdateAcross(cards), [cards]);

  const wontdo = useMemo(() => cards.filter((c) => c.status === 'wontdo').length, [cards]);
  const blockedN = useMemo(
    () => cards.filter((c) => blockerOf(c) !== '' && c.status !== 'done' && c.status !== 'wontdo').length,
    [cards],
  );
  const counted = cards.length - wontdo;

  // The lead: a STORED area lead wins over the derived plurality owner, and says
  // so ("set on the area" vs "auto") — the column is never a silent default. A
  // stored lead with no open card in the area has drifted off the work: flag it.
  const storedLead = resolved.lead;
  const leadWho = storedLead || roll.owner;
  const leadStale =
    storedLead !== '' && !cards.some((c) => c.assignee === storedLead && c.status !== 'done' && c.status !== 'wontdo');

  // The boards this area touches, most cards first — the cross-board affordance a
  // board-local detail does not need. Also the bands for the By-board pivot.
  const spans = useMemo(() => {
    const count = new Map<string, number>();
    for (const { boardId } of members) count.set(boardId, (count.get(boardId) ?? 0) + 1);
    return [...count]
      .map(([id, n]) => ({ id, name: boardName(id), cards: n }))
      .sort((a, b) => b.cards - a.cards || a.name.localeCompare(b.name));
  }, [members, boardName]);

  const cardSort = (a: Issue, b: Issue) => a.ref.localeCompare(b.ref, undefined, { numeric: true });

  type Band = { key: string; label: string; cards: Issue[] };
  const bands: Band[] = useMemo(() => {
    if (pivot === 'status') {
      return STATUS_ORDER.map((s) => ({ key: s, label: STATUS_LABELS[s], cards: cards.filter((c) => c.status === s) }))
        .filter((b) => b.cards.length > 0);
    }
    if (pivot === 'kind') {
      const present = [...new Set(cards.map((c) => c.kind))];
      const order = (k: string) => {
        const i = KIND_ORDER.indexOf(k as (typeof KIND_ORDER)[number]);
        return i === -1 ? KIND_ORDER.length : i;
      };
      return present
        .sort((a, b) => order(a) - order(b) || a.localeCompare(b))
        .map((k) => ({ key: k, label: KIND_LABEL[k] ?? k, cards: cards.filter((c) => c.kind === k) }));
    }
    // By board — the spanning boards, most cards first.
    return spans.map((b) => ({ key: b.id, label: b.name, cards: cards.filter((c) => boardOf.get(c.id) === b.id) }));
  }, [pivot, cards, spans, boardOf]);

  const renderCardRow = (c: Issue) => {
    const blocked = blockerOf(c);
    const settled = c.status === 'done' || c.status === 'wontdo';
    let mark: string;
    let markWarn = false;
    if (settled) {
      mark = STATUS_LABELS[c.status].toLowerCase();
    } else if (c.dueDate) {
      const d = daysUntil(c.dueDate, now);
      mark = d === 0 ? 'today' : d > 0 ? `${d}d` : `${-d}d over`;
      markWarn = d <= 0;
    } else {
      mark = STATUS_LABELS[c.status].toLowerCase();
    }
    return (
      <button type="button" key={c.id} className="epd-card" onClick={() => onOpenCard(boardOf.get(c.id)!, c.id)}>
        <span className="epd-ref">{c.ref}</span>
        {c.size ? <span className="epd-sz">{SIZE_LETTER[c.size]}</span> : <span className="epd-sz epd-sz-none" aria-hidden="true" />}
        <span className="epd-title">
          {c.title}
          {blocked ? <span className="epd-blocked"> · blocked: <IssueTextInline text={blocked} /></span> : null}
        </span>
        <span className="epd-cardmark">
          <span className="epd-dot" style={{ background: `var(--s-${c.status})` }} aria-hidden="true" />
          <span className={markWarn ? 'epd-mark-warn' : undefined}>{mark}</span>
        </span>
      </button>
    );
  };

  const renderBand = (b: Band) => {
    const br = rollUpEpic(b.label, b.cards, [], now);
    const bandBlocked = b.cards.filter((c) => blockerOf(c) !== '' && c.status !== 'done' && c.status !== 'wontdo').length;
    return (
      <div className="epd-band" key={b.key}>
        <div className="epd-band-head">
          <div className="epd-band-name">
            {b.label} <span className="epd-band-n">{b.cards.length}</span>
            {bandBlocked > 0 && <span className="epd-band-blocked">{bandBlocked} blocked</span>}
          </div>
          <div className="epd-band-right">
            <div className="pf-pct epd-band-pct">
              <span className="fig">{br.progressPct}%</span>
              <div className="bar"><div className="fill" style={{ width: `${br.progressPct}%` }} /></div>
            </div>
            <span className="epd-band-owner">{br.owner ? displayName(br.owner, profiles) : '—'}</span>
          </div>
        </div>
        <div className="epd-band-cards">{[...b.cards].sort(cardSort).map(renderCardRow)}</div>
      </div>
    );
  };

  const boardWord = spans.length === 1 ? 'board' : 'boards';
  const colour = resolved.colour || 'var(--t3)';

  return (
    <>
      <div className="epics-top">
        <div className="epics-crumb">
          <button type="button" className="link" onClick={onBack}>Portfolio</button>
          <span className="epics-crumb-sep" aria-hidden="true">›</span>
          <button type="button" className="link" onClick={onBack}>Areas</button>
          <span className="epics-crumb-sep" aria-hidden="true">›</span>
          <span className="epics-crumb-cur">{name}</span>
        </div>
      </div>

      <div className="epd-title-row">
        <div className="epd-title-main">
          <span className="epic-sw epd-title-sw" style={{ background: colour }} aria-hidden="true" />
          <h1 className="epd-h1">{name}</h1>
        </div>
        <span className={`rag ${HEALTH_CLS[roll.health]}`}><span className="dot" />{HEALTH_WORD[roll.health]}</span>
      </div>
      <p className="epd-subline">
        {cards.length} {cards.length === 1 ? 'card' : 'cards'} across {spans.length} {boardWord} · a part of the product
      </p>

      {spans.length > 1 && (
        <div className="epd-spans">
          <span className="epd-spans-k">Spans</span>
          {spans.map((b) => (
            <span className="epd-span" key={b.id}>{b.name} <span className="epd-band-n">{b.cards}</span></span>
          ))}
        </div>
      )}

      {latest ? (
        <div className="epd-latest">
          <div className="epd-latest-body">
            <div className="epd-latest-head">Latest update · {fmtStamp(latest.at)} · derived from {latest.ref}</div>
            <div className="epd-latest-text"><IssueTextInline text={latest.text} /></div>
            <div className="epd-latest-by">
              {latest.by ? displayName(latest.by, profiles) : 'FlightDeck'}
              {latest.nextStep ? ` · next: ${latest.nextStep}` : ''}
            </div>
          </div>
          <button type="button" className="link epd-latest-open" onClick={() => onOpenCard(boardOf.get(latest.issueId)!, latest.issueId)}>
            Open {latest.ref} →
          </button>
        </div>
      ) : (
        <div className="epd-latest epd-latest-empty">No updates yet — post one on a card and the newest shows here.</div>
      )}

      <div className="epd-cells">
        <div className="epd-cell">
          <div className="epd-cell-k">
            Owner {storedLead ? <span className="epd-lead-badge">Area lead</span> : <span className="epd-auto">auto</span>}
          </div>
          <div className="epd-cell-owner">
            {leadWho ? (
              <>
                <span className="comment-av">{initials(leadWho, profiles)}</span>
                <span className="epd-cell-v">{displayName(leadWho, profiles)}<VendorTag who={leadWho} profiles={profiles} compact /></span>
              </>
            ) : (
              <span className="epd-cell-v" style={{ color: 'var(--t3)' }}>—</span>
            )}
          </div>
          <div className={`epd-cell-sub${storedLead && leadStale ? ' epd-mark-warn' : ''}`}>
            {storedLead ? (leadStale ? 'set on the area · no open cards' : 'set on the area · not derived') : 'most cards’ owner'}
          </div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Open work</div>
          <div className="epd-cell-fig">{roll.openCount}</div>
          <div className="epd-cell-sub">
            of {cards.length} {cards.length === 1 ? 'card' : 'cards'} · {roll.doneCount} done{wontdo > 0 ? `, ${wontdo} won’t do` : ''}
          </div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Progress</div>
          <div className="epd-cell-fig">{roll.progressPct}<span className="epd-pct-sign">%</span></div>
          <div className="pf-pct epd-cell-bar"><div className="bar"><div className="fill" style={{ width: `${roll.progressPct}%` }} /></div></div>
          <div className="epd-cell-sub">{roll.doneCount} of {counted} done</div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Blocked</div>
          <div className={`epd-cell-fig${blockedN > 0 ? ' epd-mark-warn' : ''}`}>{blockedN}</div>
          <div className="epd-cell-sub">{blockedN > 0 ? 'waiting on something to clear' : 'nothing blocked'}</div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Needs attention</div>
          <div className={`epd-cell-fig${roll.needsAttention > 0 ? ' epd-mark-warn' : ''}`}>{roll.needsAttention}</div>
          <div className="epd-cell-sub">blocked or past due</div>
        </div>
      </div>

      <div className="epd-pivot">
        <div className="seg epd-pivot-seg" role="group" aria-label="Group the cards by">
          {(['board', 'status', 'kind'] as Pivot[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`seg-btn ${pivot === p ? 'active' : ''}`}
              aria-pressed={pivot === p}
              onClick={() => setPivot(p)}
            >
              {PIVOT_LABEL[p]}
            </button>
          ))}
        </div>
        <span className="epd-pivot-sub">{cards.length} {cards.length === 1 ? 'card' : 'cards'} grouped by {PIVOT_SUB[pivot]}</span>
      </div>

      <div className="epd-bands">
        {bands.length === 0 ? (
          <div className="pf-owner" style={{ color: 'var(--t3)', padding: '12px 2px' }}>No cards in this area yet.</div>
        ) : (
          bands.map(renderBand)
        )}
      </div>

      <p className="hint epics-foot">
        <b>Where the work in this part of the product stands.</b> Owner, health, progress and the open/blocked counts are
        read from the member cards; an area is a standing part of the estate, so there is no finish line and no lifecycle —
        only how much is open and how much is settled. Post updates on a card.
      </p>
    </>
  );
}
